import { useCallback, useEffect, useState } from 'react';
import { type Outcome, changePath, getJourney, resumeJourney, stopJourney } from '../api';
import type { JourneysReply } from '../api-types';

export type JourneyLoad =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; journeys: JourneysReply['journeys'] };

export type JourneyAction =
  { kind: 'stop' } | { kind: 'resume' } | { kind: 'path'; journey: string };

function run(action: JourneyAction): Promise<Outcome<JourneysReply>> {
  switch (action.kind) {
    case 'stop':
      return stopJourney();
    case 'resume':
      return resumeJourney();
    case 'path':
      return changePath(action.journey);
  }
}

/** The caller's journeys; each action answers with the updated preferences, which are shown as is. */
export function useJourney() {
  const [state, setState] = useState<JourneyLoad>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    void getJourney().then((answer) => {
      if (live)
        setState(
          answer.ok ? { status: 'ready', journeys: answer.value.journeys } : { status: 'error' },
        );
    });
    return () => {
      live = false;
    };
  }, [attempt]);

  const act = useCallback(async (action: JourneyAction): Promise<boolean> => {
    setBusy(true);
    setFailed(false);
    const answer = await run(action);
    setBusy(false);
    if (!answer.ok) {
      setFailed(true);
      return false;
    }
    setState({ status: 'ready', journeys: answer.value.journeys });
    return true;
  }, []);

  return { state, busy, failed, act, reload: () => setAttempt((n) => n + 1) };
}
